/* @ngInject */

export default () => {
  /**
   * Get Future Value (FV).
   *
   * @param {Number} rate - [required] The interest rate per period.
   * @param {Number} nper - [required] The total number of payment periods.
   * @param {Number} pmt - [required] The payment made each period. Must be entered as a negative number.
   * @param {Number} pv - [optional] The present value of future payments. If omitted, assumed to be zero. Must be entered as a negative number.
   * @param {Number} type - [optional] When payments are due. 0 = end of period, 1 = beginning of period. Default is 0.
   *
   * @returns {Number}
   */
  const getFutureValue = (rate, nper, pmt, pv, type) => {
    const t = !type ? 0 : type;
    const pow = (1 + rate) ** nper;
    let fv = 0;
    if (rate) {
      fv = (pmt * (1 + rate * t) * (1 - pow)) / rate - pv * pow;
    } else {
      fv = -1 * (pv + pmt * nper);
    }
    return fv < 0 ? -fv : -fv;
  };

  /**
   * Get Payment (PMT).
   *
   * @param {Number} rate - [required] The interest rate for the loan.
   * @param {Number} nper - [required] The total number of payments for the loan.
   * @param {Number} pv - [required] The present value, or the total amount that a series of future payments is worth now; also known as the principal.
   * @param {Number} fv - [optional] The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the
   *     future value of a loan is 0.
   * @param {Number} type - [optional] The number 0 or 1 and indicates when payments are due.
   *
   * @returns {Number}
   */
  const getPayment = (rate, nper, pv, fv, type) => {
    const futureValue = !fv ? 0 : fv;
    const t = !type ? 0 : type;
    if (rate === 0) {
      return -(pv + futureValue) / nper;
    }
    const pvif = (1 + rate) ** nper;
    let pmt = (rate / (pvif - 1)) * -(pv * pvif - futureValue);
    if (t === 1) {
      pmt /= 1 + rate;
    }
    return pmt < 0 ? -pmt : -pmt;
  };

  /**
   * Get Present Value (PV).
   *
   * @param {Number} rate - [required] The interest rate for the loan.
   * @param {Number} nper - [required] The total number of payments for the loan.
   * @param {Number} pmt - [required] The payment made each period. Must be entered as a negative number.
   * @param {Number} fv - [optional] The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the
   *     future value of a loan is 0.
   * @param {Number} type - [optional] The number 0 or 1 and indicates when payments are due.
   *
   * @returns {Number}
   */
  const getPresentValue = (rate, nper, pmt, fv) => {
    const futureValue = !fv ? 0 : fv;
    // const t = !type ? 0 : type;

    if (rate === 0) {
      return -pmt * nper - futureValue;
    }
    return (pmt / rate) * (1 - (1 + rate) ** -nper);
  };

  /**
   * Get Total Number of Payments (NPER).
   *
   * @param {Number} nper - [required] The total number of payments for the loan.
   * @param {Number} pmt - [required] The payment made each period. Must be entered as a negative number.
   * @param {Number} pv - [required] The present value, or the total amount that a series of future payments is worth now; also known as the principal.
   * @param {Number} fv - [optional] The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the
   *     future value of a loan is 0.
   * @param {Number} type - [optional] The number 0 or 1 and indicates when payments are due.
   *
   * @returns {Number}
   */
  const getRate = (nper, pmt, pv, futureValue, t, g) => {
    const guess = g || 0.01;
    const fv = futureValue || 0;
    const type = t || 0;
    const present = -pv;

    // Set maximum epsilon for end of iteration
    const epsMax = 1e-6;

    // Set maximum number of iterations
    const iterMax = 100;
    let iter = 0;
    let close = false;
    let rate = guess;

    while (iter < iterMax && !close) {
      const t1 = (rate + 1) ** nper;
      const t2 = (rate + 1) ** (nper - 1);

      const f1 = fv + t1 * present + (pmt * (t1 - 1) * (rate * type + 1)) / rate;
      const f2 = nper * t2 * present - (pmt * (t1 - 1) * (rate * type + 1)) / rate ** 2;
      const f3 = (nper * pmt * t2 * (rate * type + 1)) / rate + (pmt * (t1 - 1) * type) / rate;

      const newRate = rate - f1 / (f2 + f3);

      if (Math.abs(newRate - rate) < epsMax) {
        close = true;
      }
      iter += 1;
      rate = newRate;
    }

    if (!close) {
      return Number.NaN + rate;
    }
    return rate;
  };

  /**
   * Get Total Number of Payments (NPER).
   *
   * @param {Number} rate - [required] The interest rate for the loan.
   * @param {Number} pmt - [required] The payment made each period. Must be entered as a negative number.
   * @param {Number} pv - [required] The present value, or the total amount that a series of future payments is worth now; also known as the principal.
   * @param {Number} fv - [optional] The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the
   *     future value of a loan is 0.
   * @param {Number} type - [optional] The number 0 or 1 and indicates when payments are due.
   *
   * @returns {Number}
   */
  const getTotalNumberOfPayments = (rate, pmt, pv) => {
    const nper = -Math.log(1 - (pv / pmt) * rate) / Math.log(1 + rate);
    return Math.round(nper);
  };

  /**
   * Set Funding Worksheet Class.
   *
   * @param {Number} number
   *
   * @returns {String}
   */
  const setFundClass = (number) => {
    let fundsClass = null;
    if (number === 1) {
      fundsClass = 'fund1';
    } else if (number === 2) {
      fundsClass = 'fund2';
    } else if (number === 3) {
      fundsClass = 'fund3';
    } else {
      fundsClass = 'fundX';
    }
    return fundsClass;
  };

  return {
    getFutureValue,
    getPayment,
    getPresentValue,
    getRate,
    getTotalNumberOfPayments,
    setFundClass
  };
};
